/**
 * KpiSparkStrip — 5 KPI tiles each with a 12-week sparkline (v0.12.5).
 *
 * Reads `/api/v1/home/kpi-trends` and renders CAPA Open, Critical+Major
 * Deviations, OOS Open, Training Overdue, and Audit Findings Open as
 * tiles with an inline trend line.
 */

import { useKpiTrends } from '../../api/orgHooks';
import { Sparkline } from './Sparkline';
import styles from './HomeAnalytics.module.css';

type Band = 'green' | 'amber' | 'red';
function band(value: number, amberAt: number, redAt: number): Band {
  if (value >= redAt) return 'red';
  if (value >= amberAt) return 'amber';
  return 'green';
}

export function KpiSparkStrip() {
  const q = useKpiTrends();
  const d = q.data;

  const tiles = [
    {
      label: 'CAPA · open',
      current: d?.capa_open.current ?? 0,
      history: d?.capa_open.history.map((p) => p.value) ?? [],
      band: band(d?.capa_open.current ?? 0, 8, 15),
      hint: 'Across all severities',
    },
    {
      label: 'Deviations · CRT/MAJ',
      current: d?.dev_critical_major.current ?? 0,
      history: d?.dev_critical_major.history.map((p) => p.value) ?? [],
      band: band(d?.dev_critical_major.current ?? 0, 4, 8),
      hint: 'Under investigation',
    },
    {
      label: 'OOS · open',
      current: d?.oos_open.current ?? 0,
      history: d?.oos_open.history.map((p) => p.value) ?? [],
      band: band(d?.oos_open.current ?? 0, 3, 6),
      hint: 'Phase 1 / Phase 2',
    },
    {
      label: 'Training · overdue',
      current: d?.training_overdue.current ?? 0,
      history: d?.training_overdue.history.map((p) => p.value) ?? [],
      band: band(d?.training_overdue.current ?? 0, 3, 6),
      hint: 'Past R&U due date',
    },
    {
      label: 'Audit findings · open',
      current: d?.audit_open.current ?? 0,
      history: d?.audit_open.history.map((p) => p.value) ?? [],
      band: band(d?.audit_open.current ?? 0, 3, 6),
      hint: 'Awaiting disposition',
    },
  ];

  return (
    <section className={styles.sparkStrip} aria-label="KPI trend strip">
      {tiles.map((t) => (
        <div key={t.label} className={`${styles.sparkTile} ${styles[`band_${t.band}`]}`}>
          <div className={styles.sparkHead}>
            <span className={styles.sparkLabel}>{t.label}</span>
            <span className={styles.sparkValue}>{t.current}</span>
          </div>
          <div className={styles.sparkChart}>
            <Sparkline data={t.history} band={t.band} ariaLabel={`${t.label} 12-week trend`} />
          </div>
          <span className={styles.sparkHint}>{t.hint}</span>
        </div>
      ))}
    </section>
  );
}
